import { MaterialProductValueType } from 'api';
import { Form, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { Button, Grid } from 'ui';

import { useUpdateMaterialProduct } from '../../hooks/useUpdateMaterialProduct/useUpdateMaterialProduct';
import type { UpdateFormValues } from '../../pages/MaterialProductDetails/MaterialProductDetails';
import { type MaterialProductFormValues, MaterialProductSchema } from '../../pages/schema';
import { DeleteMaterialProductButton } from '../DeleteMaterialProductButton/DeleteMaterialProductButton';
import { MaterialProductFormSections } from '../MaterialProductFormSections/MaterialProductFormSections';

type Props = {
  materialProductId: number;
  materialProductData: UpdateFormValues | undefined;
};

const getMaterialProductFormValues = (
  materialProductData: UpdateFormValues | undefined
): MaterialProductFormValues | undefined => {
  if (!materialProductData) {
    return undefined;
  }

  return {
    buyer_material_component_values: materialProductData.buyer_material_component_values,
    country: materialProductData.country,
    creation_process: materialProductData.creation_process,
    has_caps_and_labels: materialProductData.has_caps_and_labels,
    is_food_grade: materialProductData.is_food_grade,
    material_colour_id: materialProductData.material_colour_id,
    material_grade_id: materialProductData.material_grade_id,
    material_id: materialProductData.material_id,
    material_origin_id: materialProductData.material_origin_id,
    material_spec_id: materialProductData.material_spec_id,
    seller_material_component_values: materialProductData.seller_material_component_values,
  };
};

export const MaterialProductUpdateForm = ({ materialProductId, materialProductData }: Props) => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const form = useForm<MaterialProductFormValues, unknown, UpdateFormValues>({
    resolver: yupResolver(MaterialProductSchema),
    values: getMaterialProductFormValues(materialProductData),
  });

  const { mutate: updateMaterialProduct, isPending: isUpdating } = useUpdateMaterialProduct(form);

  const onSubmit = async (values: UpdateFormValues) => {
    const sellerMaterialComponents = values.seller_material_component_values
      ? values.seller_material_component_values.map((materialComponent) => ({
          boolean_value: materialComponent.boolean_value,
          max_value: materialComponent.max_value ? materialComponent.max_value.toString() : undefined,
          min_value: materialComponent.min_value ? materialComponent.min_value.toString() : undefined,
          supported_component_id: materialComponent.supported_component_id,
          type: MaterialProductValueType.SELLER,
        }))
      : [];
    const buyerMaterialComponents = values.buyer_material_component_values
      ? values.buyer_material_component_values.map((materialComponent) => ({
          boolean_value: materialComponent.boolean_value,
          max_value: materialComponent.max_value ? materialComponent.max_value.toString() : undefined,
          min_value: materialComponent.min_value ? materialComponent.min_value.toString() : undefined,
          supported_component_id: materialComponent.supported_component_id,
          type: MaterialProductValueType.BUYER,
        }))
      : [];

    await updateMaterialProduct({
      data: { ...values, material_component_values: [...sellerMaterialComponents, ...buyerMaterialComponents] },
      id: materialProductId,
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit}>
      <MaterialProductFormSections />
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <Button loading={isUpdating} type="submit" fullWidth>
            {t('materialProducts.form.actions.update')}
          </Button>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <Button onClick={() => navigate(-1)} variant="secondary" fullWidth>
            {t('materialProducts.form.actions.cancel')}
          </Button>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <DeleteMaterialProductButton id={materialProductId} />
        </Grid>
      </Grid>
    </Form>
  );
};
